/**
 * @file AboutPage.js
 * @description Manages the About page with a prioritized professional narrative.
 * REVISION: Removed System Monitor footer.
 */

export class AboutPage {
  constructor(services) {
    this.contentManager = services.content;
    this.eventBus = services.eventBus;
    this.dom = {
      content: document.querySelector('#about .content-block'),
    };
  }

  init() {
    this.render();
    this.addListeners();
  }

  render() {
    if (!this.dom.content) return;

    // 1. DATA EXTRACTION
    const profile = this.contentManager.getProfile();
    const skills = this.contentManager.getSkills();
    const availability = this.contentManager.getAvailability();
    const certs = this.contentManager.getCertifications();
    const bio = this.contentManager.getBio();
    const experience = this.contentManager.getExperience();

    // 2. SKILL TAG GENERATOR
    const renderSkillTier = (tierData) => {
      if (!tierData?.items) return '';
      return tierData.items.map(skill => {
        const content = this.contentManager.getContentByTag(skill);
        const hasContent = content.projects.length > 0 || content.blogs.length > 0;
        return hasContent 
          ? `<a href="#" class="tag interactive" data-skill="${skill}">${skill}</a>`
          : `<span class="tag static">${skill}</span>`;
      }).join('');
    };

    // 3. TEMPLATE BLOCKS
    
    // [A] Profile Details
    const profileHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">Profile Details</h2>
        <div class="inner-block">
          <div class="detail-row"><span class="detail-label">ROLE</span><span class="detail-value">${profile.role}</span></div>
          <div class="detail-row"><span class="detail-label">LOCATION</span><span class="detail-value">${profile.location}</span></div>
        </div>
      </div>`;

    // [B] Technical Skills
    const skillsHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">Technical Skills</h2>
        <div class="inner-block" style="margin-bottom: 1rem;">
          <h3 class="subsection-title">${skills.current.label}</h3>
          <p class="experience-item-description">${skills.current.description}</p>
          <div class="profile-grid-value-tags">${renderSkillTier(skills.current)}</div>
        </div>
        <div class="inner-block">
          <h3 class="subsection-title">${skills.developing.label}</h3>
          <p class="experience-item-description">${skills.developing.description}</p>
          <div class="profile-grid-value-tags">${renderSkillTier(skills.developing)}</div>
        </div>
      </div>`;

    // [C] Availability & Logistics
    const availabilityHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">Availability & Logistics</h2>
        <div class="inner-block">
          <div class="detail-row"><span class="detail-label">INTERVIEWS</span><span class="detail-value">${availability.interviews}</span></div>
          <div class="detail-row"><span class="detail-label">START DATE</span><span class="detail-value">${availability.start_date}</span></div>
          <div class="detail-row"><span class="detail-label">TIMEZONE</span><span class="detail-value">${availability.timezone}</span></div>
        </div>
      </div>`;

    // [D] Certifications (Subtle Metadata Layout)
    const certsHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">Certifications</h2>
        ${certs.map(cert => `
          <div class="inner-block">
            <div class="cert-header-row">
              <span class="cert-title">${cert.name.toUpperCase()}</span>
              <span class="cert-status-pill ${cert.status}">${cert.status.toUpperCase()}</span>
            </div>
            <div class="cert-meta-row">
              ${cert.issuer} · ${cert.date} ${cert.url ? `· <a href="${cert.url}" target="_blank" class="subtle-link">credential ↗</a>` : ''}
            </div>
          </div>`).join('')}
      </div>`;

    // [E] About (Bio + High Contrast Button)
    const bioHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">About Me</h2>
        <div class="inner-block">
          ${bio.paragraphs.map(p => `<p class="content-block-prose">${p}</p>`).join('')}
          <div class="cta-container" style="margin-top: 1.5rem;">
            <a href="${profile.resume_url}" class="cta-primary" target="_blank">Download Resume</a>
          </div>
        </div>
      </div>`;

    // [F] Experience (Final Block - NO MONITOR)
    const experienceHtml = `
      <div class="section-wrapper">
        <h2 class="page-title">Experience</h2>
        ${experience.map(exp => `
          <div class="inner-block">
            <div class="experience-item-header">
              <h3 class="experience-item-title">${exp.title}</h3>
              <span class="experience-item-period">${exp.period}</span>
            </div>
            <div class="experience-subtext">${exp.institution || exp.organization || '[Technical Training]'}</div>
            <p class="experience-item-description">${exp.description}</p>
          </div>`).join('')}
      </div>`;

    this.dom.content.innerHTML = profileHtml + skillsHtml + availabilityHtml + certsHtml + bioHtml + experienceHtml;
  }

  addListeners() {
    this.dom.content.addEventListener('click', (e) => {
      const tag = e.target.closest('.tag.interactive');
      if (tag) {
        e.preventDefault();
        this.eventBus.emit('modal:open:skill', { skillName: tag.getAttribute('data-skill') });
      }
    });
  }
}